import { Permission, Role } from 'appwrite';
import { databases, databaseId, appwriteConfig } from '../config/appwrite';

export interface EditorDraft<T> {
  value: T;
  updatedAt: string;
}

export class DraftConflictError extends Error {
  constructor() { super('Dieser Bearbeitungsstand wurde in einem anderen Fenster geändert. Bitte sichere deinen Text und lade die Seite neu.'); }
}

// Ein privater Stand pro Benutzer und Bearbeitungsseite, unabhängig von Artikeln und Webhooks.
export class EditorDraftService {
  static async documentId(ownerId: string, editorKey: string) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify([ownerId, editorKey])));
    return `d${Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 35)}`;
  }

  static async load<T>(ownerId: string, editorKey: string): Promise<EditorDraft<T> | null> {
    try {
      const doc = await databases.getDocument(databaseId, appwriteConfig.collections.editorDrafts, await this.documentId(ownerId, editorKey));
      if (doc.ownerId !== ownerId || doc.editorKey !== editorKey) throw new Error('Bearbeitungsstand gehört zu einer anderen Sitzung.');
      const payload = JSON.parse(doc.payload);
      if (payload.version !== 1 || !payload.value || typeof payload.value !== 'object') throw new Error('Bearbeitungsstand kann nicht gelesen werden.');
      return { value: payload.value, updatedAt: doc.$updatedAt };
    } catch (error) {
      if ((error as { code?: number }).code === 404) return null;
      throw error;
    }
  }

  static async save<T>(ownerId: string, editorKey: string, value: T, expectedUpdatedAt: string | null): Promise<EditorDraft<T>> {
    const id = await this.documentId(ownerId, editorKey);
    const current = await this.load<T>(ownerId, editorKey);
    if ((current?.updatedAt ?? null) !== expectedUpdatedAt) throw new DraftConflictError();
    const payload = JSON.stringify({ version: 1, value });
    if (payload.length > 1000000) throw new Error('Der Bearbeitungsstand ist zu groß für die automatische Sicherung.');
    if (current) {
      await databases.updateDocument(databaseId, appwriteConfig.collections.editorDrafts, id, { payload });
    } else {
      try {
        await databases.createDocument(databaseId, appwriteConfig.collections.editorDrafts, id, { ownerId, editorKey, payload }, [
          Permission.read(Role.user(ownerId)), Permission.update(Role.user(ownerId)), Permission.delete(Role.user(ownerId))
        ]);
      } catch (error) {
        if ((error as { code?: number }).code === 409) throw new DraftConflictError();
        throw error;
      }
    }
    const stored = await this.load<T>(ownerId, editorKey);
    if (!stored || JSON.stringify(stored.value) !== JSON.stringify(value)) throw new Error('Die Sicherung konnte nicht bestätigt werden.');
    return stored;
  }

  static async remove(ownerId: string, editorKey: string, expectedUpdatedAt: string | null) {
    const current = await this.load(ownerId, editorKey);
    if (!current) return;
    if (current.updatedAt !== expectedUpdatedAt) throw new DraftConflictError();
    await databases.deleteDocument(databaseId, appwriteConfig.collections.editorDrafts, await this.documentId(ownerId, editorKey));
  }
}
